// @ts-nocheck
/**
 * Buying extras, as a flow rather than a grid.
 *
 * Event → team → who it's for → items. Every purchase carries the
 * registration_id of a team that is actually registered for the event, because
 * an extra attached to nobody can't be fulfilled, invoiced or shown on the Live
 * Sheet. That is why "find your team" is a step and not an afterthought.
 */
import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { getRlTeams } from "@/lib/rlTeams";
import { useAuth } from "@/contexts/AuthContext";
import { useDebounce } from "@/hooks/useDebounce";


import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  ShoppingBag, ShoppingCart, Plus, Minus, Loader2, Tag, Check, Trash2, Share2,
  Users, Video, Search, ArrowLeft, Pencil,
} from "lucide-react";
import PlayerExtraDialog, { useEventRosterPlayers } from "@/components/extras/PlayerExtraDialog";
import { displayHandle } from "@/lib/handles";


const SESSION_KEY = "rle_event_extras_cart_v1";

const money = (n: number) => `$${(Number(n) || 0).toFixed(2)}`;

const STEPS = ["Event", "Your team", "Who it's for", "Items"];

export default function EventExtrasPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const params = useParams();
  const [search] = useSearchParams();

  /** Player-extras mode: /events/:eventId/player-extras, or ?only=player. */
  const playerOnly = search.get("only") === "player" || !!params.eventId;

  const [step, setStep] = useState(1);
  const [selectedEventId, setSelectedEventId] = useState(params.eventId || search.get("event") || "");
  const [team, setTeam] = useState<any>(null);
  const [buyFor, setBuyFor] = useState<"team" | "player" | null>(playerOnly ? "player" : null);
  const [teamQuery, setTeamQuery] = useState("");
  const [searched, setSearched] = useState(false);
  /** Public lookup results — anyone can find their own registration. */
  const [lookup, setLookup] = useState<any[] | null>(null);
  const [lookupReason, setLookupReason] = useState<string | null>(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [confirmTeam, setConfirmTeam] = useState<any>(null);


  const [cart, setCart] = useState<any[]>(() => {
    try {
      const raw = sessionStorage.getItem(SESSION_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  useEffect(() => { sessionStorage.setItem(SESSION_KEY, JSON.stringify(cart)); }, [cart]);

  const [coupon, setCoupon] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState<any>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);


  /* ───── Step 1: events ───── */
  const { data: events = [], isLoading: loadingEvents } = useQuery({
    queryKey: ["ee-eligible-events"],
    queryFn: async () => {
      const { data: extras } = await (supabase as any)
        .from("event_extras").select("event_id").eq("is_active", true);
      const eventIdsWithExtras = Array.from(new Set((extras || []).map((e: any) => e.event_id)));

      const { data: catalogAll } = await (supabase as any)
        .from("extras_catalog").select("id").eq("is_active", true).eq("apply_to_all_events", true);
      const hasGlobal = (catalogAll || []).length > 0;

      const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - 7);
      let q = (supabase as any)
        .from("events")
        .select("id, name, slug, start_date, end_date, organization_id, main_venue:venues!main_venue_id(name, city, state)")
        .eq("status", "published")
        .or("is_demo.is.null,is_demo.eq.false")
        .is("deleted_at", null)
        .or("is_archived.is.null,is_archived.eq.false")
        .gte("end_date", cutoff.toISOString().split("T")[0])
        .order("start_date", { ascending: true });
      if (!hasGlobal) {
        if (eventIdsWithExtras.length === 0) return [];
        q = q.in("id", eventIdsWithExtras);
      }
      const { data: evs } = await q;
      return evs || [];
    },
  });

  const selectedEvent = events.find((e: any) => e.id === selectedEventId) || null;

  /**
   * Step 1 always renders. We preselect — one event, or an event named in the
   * URL — but never advance past the step on the buyer's behalf: a cart that
   * opens with no statement of what it is for reads as a glitch, and with more
   * than one event it reads as the wrong event silently chosen for them.
   */
  const preselected = useRef(false);
  useEffect(() => {
    if (preselected.current || selectedEventId) return;
    if (!loadingEvents && events.length === 1) {
      preselected.current = true;
      setSelectedEventId(events[0].id);
    }
  }, [loadingEvents, events, selectedEventId]);

  /* ───── Step 2: the buyer's registered teams ───── */
  const { data: myTeams = [], isLoading: loadingTeams, error: myTeamsError } = useQuery({
    queryKey: ["ee-my-registrations", selectedEventId, user?.id],
    enabled: !!selectedEventId && !!user,
    queryFn: async () => {
      const { data, error } = await (supabase as any).rpc("list_my_event_registrations", {
        p_event_id: selectedEventId,
      });
      if (error) throw error;

      const regs = data || [];
      if (!regs.length) return [];
      const rlIds = regs.map((r: any) => r.recruitlook_team_id).filter(Boolean);
      let names: Record<string, any> = {};
      try { names = rlIds.length ? await getRlTeams(rlIds) : {}; } catch { names = {}; }
      return regs.map((r: any) => {
        const rl = r.recruitlook_team_id ? names[String(r.recruitlook_team_id)] : null;
        const base = r.display_name_override || rl?.name || "Registered team";
        const division = r.division || r.age_group || null;
        return {
          registration_id: r.registration_id,
          recruitlook_team_id: r.recruitlook_team_id || null,
          team_name: base,
          division,
          display_name: division ? `${base} — ${division}` : base,
          email: r.primary_billing_email || null,
        };
      });
    },
  });

  const teamMatches = useMemo(() => {
    const q = teamQuery.trim().toLowerCase();
    if (!q) return myTeams;
    return myTeams.filter(
      (t: any) =>
        t.team_name?.toLowerCase().includes(q) ||
        t.display_name?.toLowerCase().includes(q) ||
        (t.email || "").toLowerCase().includes(q)
    );
  }, [myTeams, teamQuery]);

  /**
   * Public "find my team" lookup. No auth: the page is public and so is the
   * endpoint. Email must match exactly — a loose email match would turn this
   * into a way to discover who registered, so we never soften it client-side.
   */
  const debouncedQuery = useDebounce(teamQuery, 350);
  useEffect(() => {
    if (step !== 2 || !selectedEventId) return;
    const q = debouncedQuery.trim();
    if (!q) { setLookup(null); setLookupReason(null); return; }
    if (q.length < 3) { setLookup([]); setLookupReason("TOO_SHORT"); return; }
    let cancelled = false;
    setLookupLoading(true);
    (async () => {
      try {
        const res = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/rle-find-my-team`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
            },
            body: JSON.stringify({ event_id: selectedEventId, q }),
          }
        );
        const json = await res.json();
        if (cancelled) return;
        setLookup(json?.ok ? json.teams ?? [] : []);
        setLookupReason(json?.reason ?? null);
      } catch {
        if (!cancelled) { setLookup([]); setLookupReason("ERROR"); }
      } finally {
        if (!cancelled) setLookupLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [debouncedQuery, step, selectedEventId]);

  /** A signed-in coach with exactly one registered team skips this step — once,
   *  so pressing "Change" can actually get back to the team list. */
  const autoPickedTeam = useRef(false);
  useEffect(() => {
    if (autoPickedTeam.current || step !== 2 || team) return;
    if (user && !loadingTeams && myTeams.length === 1) {
      autoPickedTeam.current = true;
      setTeam(myTeams[0]);
      setStep(playerOnly ? 4 : 3);
    }
  }, [step, team, user, loadingTeams, myTeams, playerOnly]);

  const chooseTeam = (t: any) => {
    // Never advance on a row that can't be paid for — say so instead.
    if (!t?.registration_id) {
      toast.error("That team's registration couldn't be read — refresh and try again.");
      return;
    }
    setTeam(t);
    setConfirmTeam(null);
    setStep(playerOnly ? 4 : 3);
  };



  /* ───── Step 3/4: the items ───── */
  const { data: extras = [], isLoading: loadingExtras } = useQuery({
    queryKey: ["ee-extras", selectedEventId],
    enabled: !!selectedEventId,
    queryFn: async () => {
      const { data: eventSpecific } = await (supabase as any)
        .from("event_extras")
        .select("id, name, description, price, thumbnail_url, purchase_type, is_active, level, requires_team, requires_player, requires_handles, requires_game_date")
        .eq("event_id", selectedEventId)
        .eq("is_active", true);
      const { data: catalog } = await (supabase as any)
        .from("extras_catalog")
        .select("id, name, description, price, thumbnail_url, purchase_type, is_active, apply_to_all_events, level, requires_team, requires_player, requires_handles, requires_game_date")
        .eq("is_active", true)
        .eq("apply_to_all_events", true);

      const map = (e: any, source: "event" | "catalog") => ({
        id: source === "event" ? e.id : `cat-${e.id}`,
        name: e.name,
        description: e.description,
        price: source === "event" ? Number(e.price) || 0 : Number(e.price) / 100,
        thumbnail_url: e.thumbnail_url,
        purchase_type: e.purchase_type || "team",
        source,
        level: (e.level ?? "team") as "team" | "player",
        requires_team: !!e.requires_team,
        requires_player: (e.level ?? "team") === "player" && !!e.requires_player,
        requires_handles: !!e.requires_handles,
        requires_game_date: !!e.requires_game_date,
      });
      return [
        ...(eventSpecific || []).map((e: any) => map(e, "event")),
        ...(catalog || []).map((e: any) => map(e, "catalog")),
      ];
    },
  });

  const visibleExtras = useMemo(
    () => extras.filter((x: any) => (x.level ?? "team") === (buyFor ?? "team")),
    [extras, buyFor]
  );

  /* ───── The team's roster, for player extras ─────
     The fetch lives in the shared dialog module so there is one copy. */
  const { roster, loadingRoster, rosterFailed } = useEventRosterPlayers(
    selectedEventId,
    team?.registration_id,
    buyFor === "player"
  );

  /* ───── Cart ───── */
  const cartQty = (extraId: string) =>
    cart.filter((c) => c.extra.id === extraId && c.event_id === selectedEventId)
      .reduce((s, c) => s + c.quantity, 0);

  /**
   * Player details are captured in a dialog at the moment of adding, so a
   * player extra is always one cart line per player — never a bare quantity of
   * three with one player's details attached.
   */
  const [playerDialog, setPlayerDialog] = useState<{ extra: any; editingId?: string } | null>(null);

  const newLine = (extra: any, details: any = {}) => ({
    id: crypto.randomUUID(),
    event_id: selectedEvent.id,
    event_name: selectedEvent.name,
    extra,
    quantity: 1,
    registration_id: team.registration_id,
    team_id: team.registration_id,
    team_name: team.team_name,
    division: team.division || undefined,
    ...details,
  });

  /**
   * Player extras need a roster; handles-only team extras (e.g., team video with
   * social posting) don't. Both open the same shared details dialog.
   */
  const playerAddBlocked = loadingRoster;

  const addExtra = (extra: any) => {
    if (!selectedEvent || !team) return;
    const needsDialog = extra.requires_player || extra.requires_handles;
    if (needsDialog) {
      if (extra.requires_player && loadingRoster) return;
      setPlayerDialog({ extra });
      return;
    }

    const existing = cart.find((c) => c.extra.id === extra.id && c.event_id === selectedEvent.id);
    if (existing) {
      setCart((c) => c.map((i) => (i.id === existing.id ? { ...i, quantity: i.quantity + 1 } : i)));
      return;
    }
    setCart((c) => [...c, newLine(extra)]);
  };

  /* ?extra=<id> from the public event page: once a team is chosen, jump to the
     right buyer step for that extra's level and add it to the cart once. */
  const extraParam = search.get("extra");
  const extraApplied = useRef(false);
  const wantedExtra = extraParam ? extras.find((e: any) => e.id === extraParam) : null;
  useEffect(() => {
    if (!wantedExtra || extraApplied.current) return;
    if (step === 3 && !playerOnly) {
      setBuyFor(wantedExtra.level === "player" ? "player" : "team");
      setStep(4);
      return;
    }
    if (step === 4 && team && !loadingRoster) {
      extraApplied.current = true;
      addExtra(wantedExtra);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantedExtra, step, team, loadingRoster]);

  const submitPlayerDetails = (details: any) => {
    if (!playerDialog) return;
    const { extra, editingId } = playerDialog;
    if (editingId) {
      setCart((c) => c.map((i) => (i.id === editingId ? { ...i, ...details } : i)));
    } else {
      setCart((c) => [...c, newLine(extra, details)]);
    }
    setPlayerDialog(null);
  };

  const decExtra = (extra: any) => {
    const existing = [...cart].reverse().find((c) => c.extra.id === extra.id && c.event_id === selectedEventId);
    if (!existing) return;
    if (existing.quantity <= 1) setCart((c) => c.filter((i) => i.id !== existing.id));
    else setCart((c) => c.map((i) => (i.id === existing.id ? { ...i, quantity: i.quantity - 1 } : i)));
  };

  const updateCartItem = (id: string, patch: any) =>
    setCart((c) => c.map((i) => (i.id === id ? { ...i, ...patch } : i)));

  /** What each line is still missing — checked here, not at Checkout. */
  const missingByLine = useMemo(() => {
    const out: { id: string; name: string; missing: string[] }[] = [];
    for (const item of cart) {
      const ex = item.extra;
      const missing: string[] = [];
      if (!item.registration_id || !(item.team_name || "").trim()) missing.push("team");
      if (ex.requires_player) {
        if (!item.player_name?.trim()) missing.push("player");
        if (!item.player_jersey_number?.trim()) missing.push("jersey number");
      }
      if (ex.requires_handles) {
        if (!item.player_instagram_handle?.trim()) missing.push("Instagram");
        if (!item.player_x_handle?.trim()) missing.push("X handle");
      }
      if (ex.requires_game_date && !item.game_date) missing.push("game date");
      if (missing.length) out.push({ id: item.id, name: ex.name, missing });
    }
    return out;
  }, [cart]);


  const subtotal = cart.reduce((s, c) => s + c.extra.price * c.quantity, 0);
  const discount = appliedCoupon
    ? appliedCoupon.discount_type === "percentage"
      ? subtotal * (appliedCoupon.discount_value / 100)
      : Math.min(appliedCoupon.discount_value, subtotal)
    : 0;
  const total = Math.max(0, subtotal - discount);

  const applyCoupon = async () => {
    const code = coupon.trim();
    if (!code) return;
    const { data } = await (supabase as any)
      .from("coupons")
      .select("code, discount_type, discount_value, is_active")
      .ilike("code", code)
      .eq("is_active", true)
      .maybeSingle();
    if (!data) { toast.error("Invalid coupon"); return; }
    setAppliedCoupon({ code: data.code, discount_type: data.discount_type, discount_value: Number(data.discount_value) });
    toast.success(`Coupon ${data.code} applied`);
  };

  const checkout = async () => {
    if (cart.length === 0) return;
    for (const item of cart) {
      const ex = item.extra;
      if (!item.registration_id || !(item.team_name || "").trim()) {
        toast.error(`Pick the registered team for "${ex.name}"`);
        return;
      }
      if (ex.requires_player) {
        if (!item.player_name?.trim()) { toast.error(`Enter the player for "${ex.name}"`); return; }
        if (!item.player_jersey_number?.trim()) { toast.error(`Jersey number is required for "${ex.name}"`); return; }
      }
      if (ex.requires_handles) {
        // The media team works from these; a highlight package without them
        // can't be produced, so they're collected before payment, not after.
        if (!item.player_instagram_handle?.trim()) { toast.error(`Instagram handle is required for "${ex.name}"`); return; }
        if (!item.player_x_handle?.trim()) { toast.error(`X (Twitter) handle is required for "${ex.name}"`); return; }
      }
      if (ex.requires_game_date && !item.game_date) {
        toast.error(`Pick a game date for "${ex.name}"`);
        return;
      }
    }
    /** A parent buying for their own kid usually has no RecruitLook account.
     *  Where the cart carries a typed email we can check them out as a guest;
     *  otherwise we still need somewhere to send the package, so ask them to
     *  sign in. */
    const typedEmail = cart.map((i) => (i.buyer_email || "").trim()).find(Boolean) || "";
    const contactEmail = user?.email || typedEmail;
    if (!contactEmail) {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(cart));
      navigate(`/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`);
      return;
    }
    setSubmitting(true);
    try {
      if (total > 0) {

        const payloadExtras = cart.map((item) => {
          const ex = item.extra;
          return {
            ...(ex.source === "event" ? { extra_id: ex.id } : { name: ex.name }),
            quantity: item.quantity,
            registration_id: item.registration_id,
            team_name: item.team_name,
            division: item.division || null,
            ...(ex.requires_player ? {
              player_name: item.player_name || null,
              player_jersey_number: item.player_jersey_number || null,
            } : {}),
            ...(ex.requires_handles ? {
              player_instagram_handle: item.player_instagram_handle || null,
              player_x_handle: item.player_x_handle || null,
            } : {}),
            ...(item.game_date ? { game_date: item.game_date } : {}),
          };
        });

        const { data: sessionData } = await supabase.auth.getSession();
        const accessToken = sessionData?.session?.access_token;

        const { data, error: fnErr } = await (supabase as any).functions.invoke("create-extras-checkout", {
          body: {
            event_id: selectedEventId,
            contact_email: contactEmail,
            success_url: `${window.location.origin}/purchase/receipt?session_id={CHECKOUT_SESSION_ID}`,
            cancel_url: `${window.location.origin}${window.location.pathname}?cancelled=true`,
            extras: payloadExtras,
          },
          ...(accessToken ? { headers: { Authorization: `Bearer ${accessToken}` } } : {}),
        });
        if (fnErr) throw fnErr;
        const url = data?.checkout_url || data?.payment_url || data?.url;
        if (!url) throw new Error(data?.error || "Could not start checkout");
        setCart([]);
        sessionStorage.removeItem(SESSION_KEY);
        window.location.href = url;
        return;
      }

      if (!user) {
        // The free-claim path writes purchased_extras directly and needs a user.
        sessionStorage.setItem(SESSION_KEY, JSON.stringify(cart));
        navigate(`/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`);
        return;
      }

      const rows: any[] = [];
      for (const item of cart) {
        for (let i = 0; i < item.quantity; i++) {
          rows.push({
            event_id: item.event_id,
            extra_id: item.extra.source === "event" ? item.extra.id : null,
            registration_id: item.registration_id,
            user_id: user.id,
            buyer_email: user.email,
            extra_name: item.extra.name,
            extra_price: 0,
            team_name: item.team_name,
            division: item.division || null,
            player_name: item.player_name || null,
            player_jersey_number: item.player_jersey_number || null,
            player_instagram_handle: item.player_instagram_handle || null,
            player_x_handle: item.player_x_handle || null,
            game_date: item.game_date || null,
            qr_code: `EXTRA-${crypto.randomUUID().slice(0, 8).toUpperCase()}`,
            payment_status: "free",
            is_redeemed: false,
          });
        }
      }
      const { error } = await (supabase as any).from("purchased_extras").insert(rows);
      if (error) throw error;
      setDone(true);
      setCart([]);
      sessionStorage.removeItem(SESSION_KEY);
      toast.success("Extras claimed!");
    } catch (e: any) {
      toast.error(e.message || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12">
        <Card>
          <CardContent className="p-8 text-center space-y-4">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-100 text-green-600">
              <Check className="w-6 h-6" />
            </div>
            <h1 className="text-2xl font-bold">Extras confirmed</h1>
            <p className="text-muted-foreground">
              Your extras are in My Digital Wallet with a QR code to show at the event.
            </p>
            <div className="flex gap-2 justify-center">
              <Button asChild><Link to="/my-wallet">Open My Digital Wallet</Link></Button>
              <Button variant="outline" onClick={() => { setDone(false); setStep(1); }}>Buy more</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  /** Back always moves, and step 1 is always reachable. */
  const goBack = () => setStep((s) => Math.max(1, s - 1));

  /** Jump to any step already completed. */
  const goToStep = (n: number) => {
    if (n >= step) return;
    setStep(n);
  };


  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-10">
        <div className="text-center mb-8 max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
            <ShoppingBag className="h-4 w-4" /> {playerOnly ? "Player Media" : "Event Extras"}
          </div>
          <h1 className="text-3xl md:text-4xl font-bold">
            {playerOnly ? "Highlight Mixes & Social Packages" : "Add-Ons, Media & Insurance"}
          </h1>
          <p className="text-muted-foreground mt-3">
            Four steps: the event, your team, who it's for, then the items.
          </p>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
            <Button asChild variant="outline" size="sm"><Link to="/my-wallet">My Digital Wallet</Link></Button>
            {selectedEventId && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}/events/${selectedEventId}/player-extras`);
                  toast.success("Player-extras link copied — forward it to your parents");
                }}
              >
                <Share2 className="w-3.5 h-3.5 mr-1.5" /> Copy player link
              </Button>
            )}
          </div>
        </div>

        {/* Stepper */}
        <div className="flex items-center justify-center gap-2 mb-6 flex-wrap">
          {STEPS.map((label, i) => {
            const n = i + 1;
            return (
              <div key={label} className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => goToStep(n)}
                  disabled={n >= step}
                  className={`flex min-h-[44px] items-center gap-2 rounded-full px-3 py-1 text-base sm:text-sm ${
                    n === step ? "bg-primary text-primary-foreground"
                      : n < step ? "bg-primary/10 text-primary hover:bg-primary/20 cursor-pointer" : "bg-muted text-muted-foreground"
                  }`}
                >
                  <span className="font-semibold">{n}</span> {label}
                </button>

                {n < STEPS.length && <span className="text-muted-foreground">›</span>}
              </div>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            {step > 1 && (
              <Button variant="ghost" size="sm" onClick={goBack} className="-mb-2">
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </Button>
            )}

            {/* Step 1 — event. Always shown: preselected, never skipped. */}
            {step === 1 && (
              <Card>
                <CardContent className="p-5 space-y-3">
                  <Label className="text-base font-semibold">Which event?</Label>
                  <p className="text-sm text-muted-foreground">
                    Confirm the event these extras are for, then continue.
                  </p>
                  {loadingEvents ? (
                    <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
                  ) : events.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No events with extras available.</p>
                  ) : (
                    <>
                      <div className="space-y-2">
                        {events.map((e: any) => {
                          const active = e.id === selectedEventId;
                          return (
                            <button
                              key={e.id}
                              type="button"
                              aria-pressed={active}
                              onClick={() => setSelectedEventId(e.id)}
                              className={`w-full text-left border rounded-lg p-3 min-h-[56px] transition-colors ${
                                active
                                  ? "border-primary bg-primary/5 ring-1 ring-primary"
                                  : "hover:border-primary"
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                <div className="font-medium">{e.name}</div>
                                {active && <Check className="w-4 h-4 text-primary shrink-0" />}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {e.start_date}
                                {e.main_venue?.city ? ` · ${e.main_venue.city}, ${e.main_venue.state}` : ""}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                      <Button
                        className="w-full h-12 text-base"
                        disabled={!selectedEventId}
                        onClick={() => setStep(2)}
                      >
                        Continue
                      </Button>
                      {!selectedEventId && (
                        <p className="text-xs text-muted-foreground">Pick an event to continue.</p>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Step 2 — find your team */}
            {step === 2 && (
              <Card>
                <CardContent className="p-5 space-y-4">
                  <div>
                    <Label className="text-base font-semibold">Find your team</Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      Extras are bought for a team that's registered for {selectedEvent?.name || "this event"}.
                      Search by team name, or by the email address used to register.
                    </p>
                  </div>

                  {/* A failed prerequisite read is said out loud — a silent one
                      left this step looking simply broken. */}
                  {user && myTeamsError && (
                    <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
                      We couldn't load your registered teams
                      {(myTeamsError as any)?.message ? ` (${(myTeamsError as any).message})` : ""}. You can still
                      find your team by name or registration email below.
                    </div>
                  )}

                  {user && myTeams.length > 0 && !teamQuery.trim() && (

                    <div className="space-y-2">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        Your registered teams
                      </p>
                      {myTeams.map((t: any) => (
                        <button
                          key={t.registration_id}
                          type="button"
                          onClick={() => chooseTeam(t)}
                          className="w-full text-left border rounded-lg p-3 hover:border-primary flex items-center justify-between"
                        >
                          <div>
                            <div className="font-medium">{t.team_name}</div>
                            <div className="text-xs text-muted-foreground">
                              {t.division || "No division recorded"}
                            </div>
                          </div>
                          <span className="text-xs text-primary font-medium">Choose</span>
                        </button>
                      ))}
                    </div>
                  )}

                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      className="pl-9"
                      placeholder="Team name, or the email used to register"
                      value={teamQuery}
                      onChange={(e) => { setTeamQuery(e.target.value); setSearched(true); setConfirmTeam(null); }}
                    />
                  </div>

                  {lookupLoading && (
                    <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
                  )}

                  {!lookupLoading && lookupReason === "TOO_SHORT" && (
                    <p className="text-sm text-muted-foreground">Type at least 3 characters.</p>
                  )}

                  {!lookupLoading && lookup && lookup.length > 0 && (
                    <div className="space-y-2">
                      {lookup.map((t: any) => {
                        const pick = () =>
                          chooseTeam({
                            registration_id: t.registration_id,
                            recruitlook_team_id: t.team_id || null,
                            team_name: t.team_name,
                            division: t.division || null,
                          });
                        return (
                          <button
                            key={t.registration_id}
                            type="button"
                            onClick={pick}
                            className="w-full text-left border rounded-lg p-3 hover:border-primary"
                          >
                            <div className="flex items-center justify-between gap-3">
                              <div className="min-w-0">
                                <div className="font-medium truncate">{t.team_name}</div>
                                <div className="text-xs text-muted-foreground">
                                  {t.division || "No division recorded"}
                                </div>
                                {t.coach_email_hint && (
                                  <div className="text-[11px] text-muted-foreground mt-0.5 select-none">
                                    Registered by {t.coach_email_hint}
                                  </div>
                                )}
                              </div>
                              <span className="shrink-0 inline-flex min-h-[44px] items-center rounded-md border px-3 text-sm font-medium text-primary">
                                Choose
                              </span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}


                  {!lookupLoading && searched && teamQuery.trim().length >= 3 && lookup && lookup.length === 0 && (
                    <div className="rounded-lg border p-4 space-y-3">
                      <p className="text-sm">
                        We can't find a registered team under that name or email. Extras can only be
                        bought for a team that's registered for this event.
                      </p>
                      <Button asChild variant="outline" size="sm">
                        <Link to={`/events/${selectedEvent?.slug || selectedEventId}/register`}>
                          Register a team
                        </Link>
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Chosen team, held in view so nobody buys for the wrong squad. */}
            {step >= 3 && team && (
              <div className="flex items-center justify-between gap-3 rounded-lg border bg-muted/40 px-4 py-3">
                <div className="min-w-0">
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Buying for</p>
                  <p className="font-medium truncate">
                    {team.team_name}{team.division ? ` · ${team.division}` : ""}
                  </p>
                </div>
                <Button
                  variant="link"
                  size="sm"
                  className="shrink-0"
                  onClick={() => { setTeam(null); setConfirmTeam(null); setStep(2); }}
                >
                  Change
                </Button>
              </div>
            )}


            {/* Step 3 — team or player */}
            {step === 3 && (
              <Card>
                <CardContent className="p-5 space-y-4">
                  <div>
                    <Label className="text-base font-semibold">Who is this for?</Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      {team?.team_name}{team?.division ? ` · ${team.division}` : ""}
                    </p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => { setBuyFor("team"); setStep(4); }}
                      className="border rounded-lg p-4 text-left hover:border-primary"
                    >
                      <Users className="w-5 h-5 text-primary mb-2" />
                      <div className="font-medium">The whole team</div>
                      <div className="text-xs text-muted-foreground">Team media, insurance and add-ons.</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => { setBuyFor("player"); setStep(4); }}
                      className="border rounded-lg p-4 text-left hover:border-primary"
                    >
                      <Video className="w-5 h-5 text-primary mb-2" />
                      <div className="font-medium">One player</div>
                      <div className="text-xs text-muted-foreground">Highlight mixes and social packages.</div>
                    </button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Step 4 — items */}
            {step === 4 && (
              <Card>
                <CardContent className="p-5 space-y-4">
                  <div>
                    <Label className="text-base font-semibold">
                      {buyFor === "player" ? "Player extras" : "Team extras"}
                    </Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      {team?.team_name}{team?.division ? ` · ${team.division}` : ""}
                    </p>
                  </div>

                  {/* No roster is no longer a wall: the details are typed in
                      instead. Most buyers here are parents without an account. */}
                  {buyFor === "player" && !loadingRoster && (rosterFailed || roster.length === 0) && (
                    <div className="rounded-lg border bg-muted/40 p-3 text-sm">
                      {rosterFailed
                        ? "We couldn't load this team's roster just now — you can type the player's details in instead."
                        : "This team has no roster uploaded yet — you'll type the player's details in."}
                    </div>
                  )}

                  {loadingExtras ? (
                    <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
                  ) : visibleExtras.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      No {buyFor === "player" ? "player" : "team"} extras are offered for this event.
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {visibleExtras.map((ex: any) => {
                        const qty = cartQty(ex.id);
                        const LevelIcon = ex.level === "player" ? Video : Users;
                        return (
                          <div key={ex.id} className="border rounded-lg p-4 flex gap-4">
                            {ex.thumbnail_url ? (
                              <img src={ex.thumbnail_url} alt={ex.name} className="w-20 h-20 rounded object-cover shrink-0" />
                            ) : (
                              <div className="w-20 h-20 rounded bg-primary/10 text-primary flex items-center justify-center shrink-0">
                                <LevelIcon className="w-7 h-7" />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-3">
                                <p className="font-semibold">{ex.name}</p>
                                <span className="font-semibold whitespace-nowrap">{money(ex.price)}</span>
                              </div>
                              {/* Full description — people spend hundreds on the
                                  strength of these two or three sentences. */}
                              {ex.description && (
                                <p className="text-sm text-muted-foreground mt-1 whitespace-pre-line">
                                  {ex.description}
                                </p>
                              )}
                              <div className="flex items-center gap-2 mt-3">
                                <span className="text-[10px] uppercase tracking-wide text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                                  {ex.level === "player" ? "per player" : "per team"}
                                </span>
                                <div className="ml-auto flex items-center gap-1">
                                  {ex.requires_player || ex.requires_handles ? (
                                    <>
                                      {qty > 0 && (
                                        <span className="text-xs text-muted-foreground mr-1">{qty} in cart</span>
                                      )}
                                      {/* Each add is another player / another handle set, so it opens the form. */}
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="h-11 min-w-[88px]"
                                        disabled={ex.requires_player && playerAddBlocked}
                                        onClick={() => addExtra(ex)}
                                      >
                                        <Plus className="w-3 h-3 mr-1" /> Add
                                      </Button>

                                    </>
                                  ) : (
                                    <>
                                      {qty > 0 && (
                                        <>
                                          <Button size="icon" variant="outline" className="h-11 w-11" onClick={() => decExtra(ex)}><Minus className="w-4 h-4" /></Button>
                                          <span className="text-sm font-medium w-6 text-center">{qty}</span>
                                        </>
                                      )}
                                      <Button size="icon" variant="outline" className="h-11 w-11" onClick={() => addExtra(ex)}><Plus className="w-4 h-4" /></Button>

                                    </>
                                  )}
                                </div>

                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Cart */}
          <div className="lg:col-span-1 space-y-4">
            <Card>
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <ShoppingCart className="h-5 w-5 text-primary" />
                  <h3 className="font-semibold">Your Cart</h3>
                  <span className="ml-auto bg-primary/10 text-primary text-xs font-bold px-2 py-0.5 rounded-full">
                    {cart.reduce((s, c) => s + c.quantity, 0)}
                  </span>
                </div>

                {cart.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">No extras yet.</p>
                ) : (
                  <div className="space-y-3">
                    {cart.map((item) => {
                      const isPlayer = (item.extra.level ?? "team") === "player";
                      return (
                        <div key={item.id} className="space-y-2 py-2 border-b last:border-0">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <p className="text-sm font-medium truncate">{item.extra.name} × {item.quantity}</p>
                              <p className="text-xs text-muted-foreground truncate">
                                {item.team_name}{item.division ? ` · ${item.division}` : ""}
                              </p>
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              <span className="text-sm font-semibold">{money(item.extra.price * item.quantity)}</span>
                              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setCart((c) => c.filter((i) => i.id !== item.id))}>
                                <Trash2 className="h-3 w-3 text-destructive" />
                              </Button>
                            </div>
                          </div>

                          {/* A summary, not a form — typing happens in the dialog. */}
                          {(item.extra.requires_player || item.extra.requires_handles) && (
                            <div className="flex items-start justify-between gap-2">
                              <p className="text-xs text-muted-foreground min-w-0">
                                {(() => {
                                  if (item.player_name?.trim()) {
                                    return (
                                      <>
                                        {item.player_jersey_number ? `#${item.player_jersey_number} · ` : ""}
                                        {item.player_name}
                                        {item.player_instagram_handle ? ` · ${displayHandle(item.player_instagram_handle)}` : ""}
                                        {item.player_x_handle ? ` · ${displayHandle(item.player_x_handle)}` : ""}
                                      </>
                                    );
                                  }
                                  if (item.player_instagram_handle || item.player_x_handle) {
                                    return (
                                      <>
                                        {item.player_instagram_handle ? `Instagram: ${displayHandle(item.player_instagram_handle)}` : ""}
                                        {item.player_instagram_handle && item.player_x_handle ? " · " : ""}
                                        {item.player_x_handle ? `X: ${displayHandle(item.player_x_handle)}` : ""}
                                      </>
                                    );
                                  }
                                  if (item.extra.requires_player) return <span className="text-destructive">Player details needed</span>;
                                  if (item.extra.requires_handles) return <span className="text-destructive">Instagram / X handles needed</span>;
                                  return <span className="text-destructive">Details needed</span>;
                                })()}
                              </p>
                              <Button
                                variant="link"
                                size="sm"
                                className="h-auto p-0 text-xs shrink-0"
                                onClick={() => setPlayerDialog({ extra: item.extra, editingId: item.id })}
                              >
                                <Pencil className="w-3 h-3 mr-1" /> Edit
                              </Button>
                            </div>
                          )}


                          {item.extra.requires_game_date && (
                            <Input
                              type="date"
                              className="h-8 text-xs"
                              value={item.game_date || ""}
                              onChange={(e) => updateCartItem(item.id, { game_date: e.target.value })}
                            />
                          )}
                        </div>
                      );
                    })}

                    <div className="pt-2 border-t space-y-2">
                      <div className="flex items-center gap-2">
                        <Tag className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm font-medium">Coupon</span>
                      </div>
                      {appliedCoupon ? (
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-green-600 font-medium">✓ {appliedCoupon.code}</span>
                          <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => { setAppliedCoupon(null); setCoupon(""); }}>Remove</Button>
                        </div>
                      ) : (
                        /* Full width — the placeholder was clipping to "ode". */
                        <div className="space-y-2">
                          <Input className="h-9 text-sm w-full" placeholder="Discount code" value={coupon} onChange={(e) => setCoupon(e.target.value)} />
                          <Button size="sm" variant="outline" className="h-9 w-full" onClick={applyCoupon} disabled={!coupon.trim()}>Apply</Button>
                        </div>
                      )}

                    </div>

                    <div className="flex items-center justify-between pt-2 border-t">
                      <span className="text-sm font-semibold">Total</span>
                      <div className="text-right">
                        {discount > 0 && <span className="text-xs text-muted-foreground line-through mr-2">{money(subtotal)}</span>}
                        <span className="text-lg font-bold text-primary">{money(total)}</span>
                      </div>
                    </div>
                    {total > 0 && (
                      <p className="text-[11px] text-muted-foreground">A 3% card processing fee is added at checkout.</p>
                    )}

                    {/* An incomplete line can't be fulfilled, so it can't be paid for. */}

                    {missingByLine.length > 0 && (
                      <div className="rounded-md border border-amber-300 bg-amber-50 p-2 text-[11px] text-amber-900 space-y-0.5">
                        {missingByLine.map((m) => (
                          <p key={m.id}>
                            <span className="font-medium">{m.name}</span> is missing {m.missing.join(", ")}.
                          </p>
                        ))}
                      </div>
                    )}

                    <Button
                      className="w-full"
                      onClick={checkout}
                      disabled={submitting || missingByLine.length > 0}
                    >
                      {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</> : (
                        total === 0 ? "Claim Free Extras" : `Checkout · ${money(total)}`
                      )}
                    </Button>
                    {!user && !cart.some((i: any) => (i.buyer_email || "").trim()) && (
                      <p className="text-[11px] text-muted-foreground text-center">You'll be asked to sign in at checkout.</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <PlayerExtraDialog
        open={!!playerDialog}
        onOpenChange={(o) => { if (!o) setPlayerDialog(null); }}
        extra={playerDialog?.extra ?? null}
        teamLabel={`${team?.team_name ?? ""}${team?.division ? ` · ${team.division}` : ""}`}
        roster={roster}
        loadingRoster={loadingRoster}
        rosterFailed={rosterFailed}
        collectContact={!user}
        defaultTeamName={team?.team_name || ""}
        defaultEmail={user?.email || cart.map((i: any) => i.buyer_email).find(Boolean) || ""}
        initial={
          playerDialog?.editingId
            ? cart.find((i) => i.id === playerDialog.editingId) ?? null
            : null
        }
        onSubmit={submitPlayerDetails}
      />
    </div>
  );

}
